import { z } from "zod";
import {
  ActivityDistributionSchema,
  ActivitySpreadSchema,
  EventDashboardSchema,
  ResponseTimeSchema,
  type EventDashboard,
} from "./event-dashboard.schemas.js";

export const AdminResponseTimeSchema = ResponseTimeSchema.extend({
  p25Ms: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Admin-only. Nearest-rank p25 of availability to first seen over opened requests, in ms (null when none opened)",
    ),
  p75Ms: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Admin-only. Nearest-rank p75 of availability to first seen over opened requests, in ms (null when none opened)",
    ),
  p90Ms: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe(
      "Admin-only. Nearest-rank p90 of availability to first seen over opened requests, in ms (null when none opened)",
    ),
});

export const AdminActivityDistributionSchema =
  ActivityDistributionSchema.extend({
    p75: z
      .number()
      .int()
      .min(0)
      .nullable()
      .describe(
        "Admin-only. Nearest-rank 75th percentile over joined participants, zeros included (null when there are no joined participants)",
      ),
    p90: z
      .number()
      .int()
      .min(0)
      .nullable()
      .describe(
        "Admin-only. Nearest-rank 90th percentile over joined participants, zeros included (null when there are no joined participants)",
      ),
    max: z
      .number()
      .int()
      .min(0)
      .describe(
        "Admin-only. Largest value of any joined participant (0 when none)",
      ),
    total: z
      .number()
      .int()
      .min(0)
      .describe("Admin-only. Sum of the value over joined participants"),
  });

export const AdminActivitySpreadSchema = ActivitySpreadSchema.extend({
  sent: AdminActivityDistributionSchema.describe(
    "Non-cancelled requests sent per joined participant",
  ),
  received: AdminActivityDistributionSchema.describe(
    "Non-cancelled requests received per joined participant",
  ),
  connections: AdminActivityDistributionSchema.describe(
    "Accepted requests, either direction, per joined participant",
  ),
});

/**
 * Admin tier of `GET /api/v1/events/:eventIdPublic/dashboard`: the host
 * dashboard plus the percentile and max fields only an admin may see.
 */
export const AdminEventDashboardSchema = EventDashboardSchema.extend({
  responseTime: AdminResponseTimeSchema.describe(
    "Time to open delivered requests, from max(created, recipient first JOINED) to first seen",
  ),
  activitySpread: AdminActivitySpreadSchema.describe(
    "Per-person sent, received and connections spread over currently-JOINED participants",
  ),
});

export type AdminResponseTime = z.infer<typeof AdminResponseTimeSchema>;
export type AdminActivityDistribution = z.infer<
  typeof AdminActivityDistributionSchema
>;
export type AdminActivitySpread = z.infer<typeof AdminActivitySpreadSchema>;
export type AdminEventDashboard = z.infer<typeof AdminEventDashboardSchema>;

/**
 * Both tiers of `GET /api/v1/events/:eventIdPublic/dashboard` compute the same
 * dashboard; which schema encodes the response is decided by the actor's
 * role, not the route.
 */
export type EventDashboardResult = AdminEventDashboard | EventDashboard;
